// Bounded-concurrency runner for bulk uploads. A folder drop can hold
// hundreds of files; firing one POST per file simultaneously would flood the
// browser's connection pool and the backend's request threads, while strictly
// sequential uploads waste minutes. A small fixed window is the middle ground
// — the ingestion job queue paces the expensive work server-side regardless.

export interface RetryOptions {
  /** Total tries, including the first. */
  attempts: number;
  baseDelayMs: number;
  maxDelayMs: number;
  isRetryable: (error: unknown) => boolean;
  /** Injectable for tests; defaults to a real timer. */
  sleep?: (ms: number) => Promise<void>;
}

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Retry a transient failure with exponential backoff and jitter. Exists for
 * the ingress rate limiter: a bulk upload is *expected* to overrun it, and
 * the correct client behavior is to slow down to the server's pace, not to
 * fail the file. Delay for attempt n is in [cap/2, cap] where
 * cap = min(maxDelayMs, baseDelayMs * 2^(n-1)).
 */
export async function withRetries<R>(fn: () => Promise<R>, options: RetryOptions): Promise<R> {
  const sleep = options.sleep ?? realSleep;
  for (let attempt = 1; ; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt >= options.attempts || !options.isRetryable(error)) throw error;
      const cap = Math.min(options.maxDelayMs, options.baseDelayMs * 2 ** (attempt - 1));
      await sleep(cap / 2 + Math.random() * (cap / 2));
    }
  }
}

export type SettledResult<R> =
  | { ok: true; value: R }
  | { ok: false; error: unknown };

export interface RunQueueOptions<R> {
  concurrency: number;
  /** Called once per item as it settles, in completion order. */
  onSettled?: (index: number, result: SettledResult<R>) => void;
}

export async function runQueue<T, R>(
  items: readonly T[],
  worker: (item: T, index: number) => Promise<R>,
  options: RunQueueOptions<R>,
): Promise<SettledResult<R>[]> {
  const results: SettledResult<R>[] = new Array(items.length);
  let next = 0;

  async function drain(): Promise<void> {
    while (next < items.length) {
      const index = next++;
      const item = items[index]!; // in-bounds: index < items.length
      let result: SettledResult<R>;
      try {
        result = { ok: true, value: await worker(item, index) };
      } catch (error) {
        result = { ok: false, error };
      }
      results[index] = result;
      options.onSettled?.(index, result);
    }
  }

  const lanes = Math.max(1, Math.min(options.concurrency, items.length));
  await Promise.all(Array.from({ length: lanes }, () => drain()));
  return results;
}
